import React, { createContext, useState, useContext, useEffect } from 'react';
import { apiClient, api } from '../services/api';
import { setDraftAccount } from 'utils/draftAccountScope';
import { draftAccountSnapshot } from 'utils/draftAccountScope';
import { claimGuestDraft } from 'utils/draftStore';

// Safe helpers to avoid runtime errors if apiClient isn't initialized for any reason
const setAuthHeader = (token) => {
  try {
    if (apiClient && apiClient.defaults && apiClient.defaults.headers) {
      apiClient.defaults.headers.common = apiClient.defaults.headers.common || {};
      if (token) apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      else delete apiClient.defaults.headers.common['Authorization'];
    }
  } catch { }
};

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const hasAuthCookie = () => {
  try {
    return typeof document !== 'undefined' && document.cookie && document.cookie.includes('token=');
  } catch {
    return false;
  }
};

// A storage value is an account-change hint only, never authentication.
const storedUserIdentity = (value) => {
  try {
    const user = JSON.parse(value);
    const id = Number(user?.id);
    return Number.isSafeInteger(id) && id > 0 && typeof user?.role === 'string'
      ? `${id}:${user.role}` : null;
  } catch { return null; }
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(() => { setDraftAccount(null, false); return null; });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true; // Flag to track if component is mounted
    let reloading = false;
    const profileRequest = new AbortController();
    const invalidateSession = () => {
      if (reloading) return;
      reloading = true;
      isMounted = false; // Fence old profile success AND credential cleanup.
      profileRequest.abort();
      setDraftAccount(null, false);
      setCurrentUser(null);
      setLoading(true);
      // Remount account-scoped pages/recovery against a fresh server profile.
      // Do not logout, trust cached users, migrate drafts or resend commands.
      window.location.reload();
    };
    const sessionChanged = (event) => {
      if (reloading || event.storageArea !== window.localStorage) return;
      const changed = event.key === null ||
        (event.key === 'token' && event.oldValue !== event.newValue) ||
        (event.key === 'user' && storedUserIdentity(event.oldValue) !== storedUserIdentity(event.newValue));
      if (changed) invalidateSession();
    };
    window.addEventListener('storage', sessionChanged);

    const loadUser = async () => {
      try {
        const savedToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
        const savedIdentity = storedUserIdentity(localStorage.getItem('user'));
        const profileReadCurrent = () => {
          if (!isMounted || profileRequest.signal.aborted) return false;
          // Storage events are asynchronous: also fence before a queued event
          // can run, so an old response cannot clear or replace a new login.
          if (localStorage.getItem('token') !== savedToken ||
              storedUserIdentity(localStorage.getItem('user')) !== savedIdentity) {
            invalidateSession();
            return false;
          }
          return true;
        };

        // If neither token nor auth-cookie exists, skip server calls entirely
        if (!savedToken && !hasAuthCookie()) {
          setDraftAccount(null);
          setCurrentUser(null);
          try { localStorage.removeItem('user'); } catch { }
          setLoading(false);
          return;
        }

        // Only apply auth header if we actually have a token
        if (savedToken) {
          setAuthHeader(savedToken);
        }

        // Always try to fetch fresh profile; server will use cookie and/or bearer token
        let me = null;
        try {
          me = await api.auth.profile({ signal: profileRequest.signal });
          if (!profileReadCurrent()) return;
          if (me?.is_blocked) {
            me = null; // Treat as not logged in
            try {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
            } catch { }
            setAuthHeader(null);
          }
        } catch (e) {
          if (!profileReadCurrent()) return;
          me = null;
          // If we had no token AND no cookie, skip logging a real error; otherwise clear stale creds
          if (savedToken || hasAuthCookie()) {
            try {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
            } catch { }
            setAuthHeader(null);
          }
        }

        if (isMounted) { // Only update state if component is still mounted
          const finalUser = me; // do not fall back to cached user if profile failed
          setDraftAccount(finalUser);
          setCurrentUser(finalUser || null);
          if (finalUser) {
            try { localStorage.setItem('user', JSON.stringify(finalUser)); } catch { }
          } else {
            try { localStorage.removeItem('user'); } catch { }
          }
        }
      } catch {
        if (isMounted) { // Only update state if component is still mounted
          setDraftAccount(null);
          setCurrentUser(null);
          try { localStorage.removeItem('user'); } catch { }
        }
      } finally {
        if (isMounted) { // Always set loading to false if component is mounted
          setLoading(false);
        }
      }
    };

    loadUser();

    return () => {
      isMounted = false; // Set flag to false when component unmounts
      window.removeEventListener('storage', sessionChanged);
      profileRequest.abort();
    };
  }, []); // Empty dependency array means it runs once on mount

  const login = async ({ email, password }) => {
    const previousDraftAccount = draftAccountSnapshot();
    try {
      const res = await apiClient.post('/auth/login', { email, password });
      const data = res?.data || {};

      if (data.twoFactorRequired) {
        setDraftAccount(null);
        localStorage.removeItem('token');localStorage.removeItem('user');setAuthHeader(null);setCurrentUser(null);
        return { success: false, twoFactorRequired: true };
      }

      const user = data.user;
      const token = data.token;

      if (user?.is_blocked) {
        return { success: false, code: 'user_blocked', message: 'Din konto er blokeret.' };
      }

      if (token) {
        try { localStorage.setItem('token', token); } catch { }
        setAuthHeader(token);
      }
      if (user) {
        setDraftAccount(user);
        try { localStorage.setItem('user', JSON.stringify(user)); } catch { }
        setCurrentUser(user);
        await claimGuestDraft(previousDraftAccount);
        return { success: true };
      }

      return { success: false, code: 'unexpected_response', message: 'Uventet svar fra serveren' };
    } catch (error) {
      const status = error?.response?.status;
      const code = error?.response?.data?.error;

      if (code === 'email_not_verified') {
        return { success: false, code, message: 'Din e-mail er ikke bekræftet.' };
      }
      if (code === 'user_blocked') {
        return { success: false, code, message: 'Din konto er blokeret.' };
      }
      if (status === 401 || code === 'Invalid credentials') {
        return { success: false, code: 'invalid_credentials', message: 'Forkert email eller adgangskode.' };
      }
      if (code === 'account_deleted') {
        return { success: false, code: 'account_deleted', message: error?.response?.data?.message || 'Din konto er markeret til sletning.' };
      }

      return {
        success: false,
        code: code || 'login_failed',
        message: error?.message || 'Kunne ikke logge ind. Prøv igen.',
      };
    }
  };

  const after2fa = async () => {
    const previousDraftAccount = draftAccountSnapshot();
    try {
      const me = await api.auth.profile();
      setDraftAccount(me);
      setCurrentUser(me || null);
      if (me) await claimGuestDraft(previousDraftAccount);
      if (me) {
        localStorage.setItem('user', JSON.stringify(me));
      } else {
        localStorage.removeItem('user');
      }
      return { success: true };
    } catch {
      return { success: false };
    }
  };

  const register = async (userData) => {
    try {
      const res = await api.auth.register(userData);
      // Do not mark as logged in on registration; require explicit login
      return { success: true, verifySent: !!res?.verifySent };
    } catch (error) {
      const status = error?.response?.status;
      const msg = error?.response?.data?.error || 'Kunne ikke oprette bruger';
      if (status === 409) {
        throw new Error('email_in_use');
      }
      throw new Error(msg);
    }
  };

  const restoreAccount = async ({ email, password }) => {
    const previousDraftAccount = draftAccountSnapshot();
    try {
      const res = await apiClient.post('/auth/restore', { email, password });
      const data = res?.data || {};

      if(data.loginRequired){
        localStorage.removeItem('token');localStorage.removeItem('user');setAuthHeader(null);setCurrentUser(null);
        return {success:true,loginRequired:true,message:data.message};
      }

      const user = data.user;
      const token = data.token;

      if (token) {
        try { localStorage.setItem('token', token); } catch { }
        setAuthHeader(token);
      }
      if (user) {
        setDraftAccount(user);
        try { localStorage.setItem('user', JSON.stringify(user)); } catch { }
        setCurrentUser(user);
        await claimGuestDraft(previousDraftAccount);
        return { success: true, message: data.message };
      }
      return { success: false, message: 'Uventet svar ved genoprettelse' };
    } catch (error) {
      throw new Error(error?.response?.data?.message || error?.message || 'Kunne ikke genoprette konto');
    }
  };

  const logout = async () => {
    try { await api.auth.logout(); } catch { }
    setDraftAccount(null);
    setCurrentUser(null);
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch { }
    setAuthHeader(null);
  };

  const updateUser = (u) => {
    if (!u) return;
    setCurrentUser((prev) => { const next = { ...(prev || {}), ...u }; setDraftAccount(next); return next; });
    localStorage.setItem('user', JSON.stringify({ ...(JSON.parse(localStorage.getItem('user') || '{}')), ...u }));
  };

  const value = { currentUser, loading, login, after2fa, register, logout, updateUser, restoreAccount };
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
